import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Play, 
  Clock,
  Star,
  Users,
  ChevronRight,
  CheckCircle,
  Monitor,
  Award,
  Sparkles,
  Hand,
  Eye,
  Scissors,
  Flame
} from "lucide-react";
import InquiryForm from "@/components/InquiryForm";

const onDemandCourses = [
  {
    id: "facial_basics",
    category: "Esthetics",
    title: "Facial Fundamentals",
    instructor: "LuxuryBeauty Academy",
    rating: 4.8,
    students: 120,
    duration: "6 hours",
    lessons: 24,
    price: 15000,
    icon: Sparkles,
    image: "https://images.pexels.com/photos/11896310/pexels-photo-11896310.jpeg?auto=compress&cs=tinysrgb&w=600",
    description: "Learn the fundamentals of facial treatments from home. Perfect for beginners.",
    curriculum: [
      "Introduction to Skin Types",
      "Basic Facial Techniques",
      "Product Knowledge",
      "Client Consultation",
      "Sanitation & Safety"
    ],
    featured: true
  },
  {
    id: "nail_art_basics",
    category: "Nails",
    title: "Nail Art for Beginners",
    instructor: "LuxuryBeauty Academy",
    rating: 4.9,
    students: 95,
    duration: "4 hours",
    lessons: 16,
    price: 12000,
    icon: Hand,
    image: "https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=600",
    description: "Master basic nail art techniques and designs you can offer to clients.",
    curriculum: [
      "Nail Prep Essentials",
      "Color Theory",
      "Simple Designs",
      "French Tips",
      "Finishing Techniques"
    ],
    featured: true
  },
  {
    id: "lash_theory",
    category: "Lashes",
    title: "Lash Extension Theory",
    instructor: "LuxuryBeauty Academy",
    rating: 4.7,
    students: 78,
    duration: "3 hours",
    lessons: 12,
    price: 10000,
    icon: Eye,
    image: "https://images.pexels.com/photos/3985329/pexels-photo-3985329.jpeg?auto=compress&cs=tinysrgb&w=600",
    description: "Understand the theory behind lash extensions before hands-on training.",
    curriculum: [
      "Lash Anatomy",
      "Extension Types",
      "Adhesive Science",
      "Mapping Techniques",
      "Aftercare Education"
    ],
    featured: false
  },
  {
    id: "business_basics",
    category: "Business",
    title: "Starting Your Beauty Business",
    instructor: "LuxuryBeauty Academy",
    rating: 4.9,
    students: 150,
    duration: "5 hours",
    lessons: 20,
    price: 18000,
    icon: Award,
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=600",
    description: "Everything you need to know to start and grow your beauty business.",
    curriculum: [
      "Business Planning",
      "Pricing Your Services",
      "Marketing Basics",
      "Social Media Strategy",
      "Client Retention"
    ],
    featured: true
  },
  {
    id: "candle_intro",
    category: "Skills Academy",
    title: "Introduction to Candle Making",
    instructor: "LuxuryBeauty Academy",
    rating: 4.8,
    students: 65,
    duration: "2 hours",
    lessons: 8,
    price: 8000,
    icon: Flame,
    image: "https://images.pexels.com/photos/3270223/pexels-photo-3270223.jpeg?auto=compress&cs=tinysrgb&w=600",
    description: "Get started with candle making basics before joining our full course.",
    curriculum: [
      "Wax Types Overview",
      "Essential Tools",
      "Safety Guidelines",
      "Basic Techniques",
      "Your First Candle"
    ],
    featured: false
  },
];

const categories = ["All", "Esthetics", "Nails", "Lashes", "Business", "Skills Academy"];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const OnDemandPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [expandedCourse, setExpandedCourse] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredCourses = selectedCategory === "All" 
    ? onDemandCourses 
    : onDemandCourses.filter(c => c.category === selectedCategory);

  const featuredCourses = onDemandCourses.filter(c => c.featured);

  return (
    <div data-testid="ondemand-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Learn at Your Own Pace
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              On-Demand<br />
              <span className="gold-gradient">Training</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Access our video courses anytime, anywhere. Learn beauty skills and 
              business strategies from home before joining our hands-on programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-[#0A0A0A] border-y border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Monitor, text: "Watch Anywhere" },
              { icon: Clock, text: "Lifetime Access" },
              { icon: Award, text: "Certificate Included" },
              { icon: Users, text: "Community Support" },
            ].map((feature) => (
              <div key={feature.text} className="flex items-center justify-center gap-3 text-[#A3A3A3]">
                <feature.icon size={20} className="text-[#C5A059]" />
                <span className="font-body text-sm">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="featured-courses">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Most Popular
              </span>
              <h2 className="font-heading text-2xl md:text-3xl text-white mt-2">
                Featured Courses
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {featuredCourses.map((course) => (
              <AnimatedSection key={course.id}>
                <div 
                  className="bento-card overflow-hidden group cursor-pointer"
                  onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                  data-testid={`featured-course-${course.id}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={course.image} 
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                    <div className="absolute top-3 left-3 bg-[#C5A059] text-[#050505] text-xs font-semibold px-2 py-1">
                      {course.category}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-[#050505]/80 text-white text-xs px-2 py-1 flex items-center gap-1">
                      <Play size={12} />
                      {course.lessons} lessons
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-heading text-lg text-white mb-2">{course.title}</h3>
                    <p className="font-body text-sm text-[#A3A3A3] mb-3">{course.description}</p>
                    
                    <div className="flex items-center justify-between text-sm mb-3">
                      <div className="flex items-center gap-1 text-[#C5A059]">
                        <Star size={14} fill="currentColor" />
                        <span>{course.rating}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#A3A3A3]">
                        <Users size={14} />
                        <span>{course.students} students</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#A3A3A3]">
                        <Clock size={14} />
                        <span>{course.duration}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-[#262626]">
                      <span className="font-heading text-xl text-[#C5A059]">
                        ${course.price.toLocaleString()} <span className="text-xs text-[#A3A3A3]">GYD</span>
                      </span>
                      <span className="text-[#C5A059] text-sm">View Details →</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* All Courses */}
      <section className="py-16 md:py-24 bg-[#050505]" data-testid="all-courses">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-white">
                All Courses
              </h2>
            </div>
          </AnimatedSection>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
                className={`font-body text-sm tracking-wider px-5 py-2 transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-[#C5A059] text-[#050505]"
                    : "text-[#A3A3A3] border border-[#262626] hover:border-[#C5A059]"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Course List */}
          <div className="space-y-4">
            {filteredCourses.map((course) => (
              <AnimatedSection key={course.id}>
                <div 
                  className={`bento-card p-0 overflow-hidden cursor-pointer transition-all duration-300 ${
                    expandedCourse === course.id ? 'border-[#C5A059]' : ''
                  }`}
                  onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                  data-testid={`course-${course.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-48 h-32 md:h-auto flex-shrink-0">
                      <img 
                        src={course.image} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[#C5A059] text-xs tracking-wider uppercase">{course.category}</span>
                            <div className="flex items-center gap-1 text-[#C5A059] text-sm">
                              <Star size={12} fill="currentColor" />
                              <span>{course.rating}</span>
                            </div>
                          </div>
                          <h3 className="font-heading text-lg text-white mb-1">{course.title}</h3>
                          <p className="font-body text-sm text-[#A3A3A3]">{course.description}</p>
                          
                          <div className="flex items-center gap-4 mt-3 text-xs text-[#A3A3A3]">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {course.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <Play size={12} />
                              {course.lessons} lessons
                            </span>
                            <span className="flex items-center gap-1">
                              <Users size={12} />
                              {course.students}
                            </span>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className="font-heading text-2xl text-[#C5A059]">
                            ${course.price.toLocaleString()}
                          </span>
                          <span className="text-xs text-[#A3A3A3] block">GYD</span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedCourse === course.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-4 pt-4 border-t border-[#262626]"
                        >
                          <h4 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-3">
                            What You'll Learn
                          </h4>
                          <div className="grid md:grid-cols-2 gap-2">
                            {course.curriculum.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                                <CheckCircle size={14} className="text-[#C5A059]" />
                                {item}
                              </div>
                            ))}
                          </div>
                          <Link
                            to="/contact"
                            className="inline-block mt-4 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-wider uppercase px-6 py-3 hover:brightness-110 transition-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Enroll Now
                          </Link>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="ondemand-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Ready to Start Learning?
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Enroll Today
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
                  Get instant access to our on-demand courses. Learn at your own pace, 
                  from anywhere. Perfect preparation for our hands-on training programs.
                </p>
                <div className="space-y-3">
                  {[
                    "Instant access after enrollment",
                    "Watch on any device",
                    "Downloadable resources",
                    "Certificate of completion",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-[#C5A059]" />
                      <span className="font-body text-[#A3A3A3]">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex gap-4">
                  <Link
                    to="/courses"
                    className="border border-[#C5A059] text-[#C5A059] font-semibold text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-[#C5A059]/10 transition-all duration-300 inline-flex items-center gap-2"
                  >
                    View In-Person Courses
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>

              <div className="bento-card p-8">
                <h3 className="font-heading text-xl text-white mb-6">Contact for Enrollment</h3>
                <InquiryForm inquiryType="ondemand" />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default OnDemandPage;
